import { FunctionComponent } from 'react'

export type SubmenuItem = {
  id: string,
  icon: FunctionComponent,
  title: string,
  subPath: string,
}

export interface MenuItem {
  title: string,
  Icon: FunctionComponent,
  path: string,
  id: string,
}

export interface Membership {
  title: string;
  id: string;
  uuid: string;
  activeSubscriptionCount: number;
  arr: string;
  status: string;
  membershipType?: string;
  organizationSlug?: string;
  walletEnabled?: boolean;
  /** Set on the starter membership we suggest an org begins with. */
  recommended?: boolean;
  /** Cents. Present even when the price is still inactive (draft membership). */
  monthlyAmount?: number | null;
  annualAmount?: number | null;
  benefitCount?: number;
}

export interface PatronSimple {
  email: string;
  id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}
export interface Patron extends PatronSimple {
  subscriptions?: Subscription[];
  tags?: PatronTag[];
  shirtSize?: string;
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  state?: string;
  zip?: string;
  createdAt?: string;
}

export interface EmployeeParams {
  name: string;
  archivedAt?: Date;
}
export interface BenefitParams {
  title: string;
  details?: string;
  fine_print?: string;
  interval: string;
  quantity: number;
  days_valid?: number;
  restriction?: string;
}

export interface PricingParams {
  amount: number;
  interval: string;
  nickname?: string;
  tax_rate_ids?: number[];
}

export interface MembershipCreateParams {
  membership: {
    title: string;
    description?: string;
    fine_print?: string;
    image_url?: string;
    collect_address?: boolean;
    collect_shirt_size?: boolean;
    collect_birthdate?: boolean;
    max_secondaries?: number;
    membership_type?: string;
  };
  benefits?: BenefitParams[];
  pricing?: PricingParams[];
}

export interface MembershipParams {
  title: string;
}

export interface TaxRate {
  id: number;
  display_name: string;
  rate: number;
  jurisdiction?: string;
  description?: string;
}

export interface Employee extends EmployeeParams {
  id: string;
  createdAt: Date;
}

export interface BackendUserParams {
  email: string;
  phone?: string;
  timeZone?: string;
  notificationsOn: boolean;
}

export interface BackendUser extends BackendUserParams {
  id: string;
  uuid: string;
  role: string;
  createdAt: string;
}

export enum BackendUserSortableId {
  email = 'email',
  role = 'role',
}

export interface BackendUserSortState {
  key: BackendUserSortableId,
  ascending: boolean,
  active: boolean,
}

export type PatronTag = Record<string, string>

export interface Benefit {
  id: string,
  title: string,
  details: string,
  quantity: number,
}

export interface BenefitCycle {
  id: string,
  patronageBenefitIds: string[],
  patronageBenefits?: PatronBenefit[],
  status: string,
  nextBenefitCycleId?: string,
  previousBenefitCycleId?: string,
  startDate: string,
  endDate: string,
}

export interface SubscriptionSimple {
  id: string;
  membershipTitle: string;
  status: string;
  currentCycleEnd: string;
  startDate: string;
  patronName?: string;
  autoRenew: boolean;
  patron: Patron;
  price: string;
  interval: string;
}
export interface Subscription extends SubscriptionSimple {
  benefitCycles: Record<string, BenefitCycle>;
  currentBenefitCycleId: string;
  benefits: PatronBenefit[];
  renewalText?: string;
  membership: Membership;
}


export interface PatronBenefit {
  id: string,
  uuid: string,
  startDate: string,
  name: string,
  redeemedAt: string,
  issuedOn: string,
  expirationDate: string,
  qty: number,
  redemptions: any[]
  status: ('available' | 'redeemed' | 'expired'),
}

export interface PatronSubscription {
  id: string,
  interval: ('month' | 'year' | 'once'),
  name: string,
  renewalDate: string,
  status: ('active' | 'canceled' | 'pending_cancel' | 'pending_active'),
  benefits: Benefit[],
  patronBenefits: Benefit[],
}

export enum PatronSortableId {
  firstName = 'firstName',
  lastName = 'lastName',
  phone = 'phone',
  email = 'email',
}

export interface SortState<T> {
  key: keyof T,
  ascending: boolean,
}

export enum EmployeeSortableId {
  name = 'name',
}

export interface EmployeeSortState {
  key: EmployeeSortableId,
  ascending: boolean,
  active: boolean,
}

export interface RecentMember {
  id: number;
  patronName: string;
  patronEmail: string;
  patronPhone: string;
  membershipTitle: string;
  joinedDate: string;
  interval: string;
  mrr: string;
}

export interface AtRiskPatron {
  id: number;
  patronName: string;
  patronEmail: string;
  membershipTitle: string;
  lastRedemptionDate?: string | null;
  joinedDate?: string;
}

export interface TopBenefit {
  benefitId: number;
  benefitTitle: string;
  redemptionCount: number;
}

export interface MostPopularMembership {
  id: number;
  title: string;
  subscriberCount: number;
}

export interface UpcomingRenewal {
  id: number;
  patronName: string;
  patronEmail: string;
  patronPhone: string;
  membershipTitle: string;
  renewalDate: string;
  interval: string;
  mrr: string;
}

export interface MonthlyRevenue {
  month: string;
  revenue: number;
}
